import React from 'react';

/**
 * Reusable month-view calendar.
 *
 * Props:
 *  - year, month        : the visible month (month is 0-indexed, JS Date style)
 *  - events              : [{ date: 'YYYY-MM-DD', label, tone }]  tone: 'green' | 'blue' | 'red'
 *  - selectedDate         : 'YYYY-MM-DD' | null
 *  - onSelectDate(dateStr)
 *  - onMonthChange(year, month)
 *  - maxEventsPerDay     : how many event pills to show before collapsing to "+N more" (default 2)
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const TONE_STYLES = {
  green: { bg: '#d1fae5', color: '#065f46' },
  blue:  { bg: '#dbeafe', color: '#1e40af' },
  red:   { bg: '#fee2e2', color: '#991b1b' },
};

const toISODate = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const buildGrid = (year, month) => {
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = firstOfMonth.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i++) {
    const d = daysInPrevMonth - startOffset + 1 + i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ date: toISODate(y, m, d), day: d, otherMonth: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: toISODate(year, month, d), day: d, otherMonth: false });
  }
  let nextDay = 1;
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    cells.push({ date: toISODate(y, m, nextDay), day: nextDay, otherMonth: true });
    nextDay++;
    if (cells.length >= 42) break;
  }
  return cells;
};

const AppointmentCalendar = ({
  year,
  month,
  events = [],
  selectedDate = null,
  onSelectDate = () => {},
  onMonthChange = () => {},
  maxEventsPerDay = 2,
}) => {
  const todayStr = toISODate(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const cells = buildGrid(year, month);

  const eventsByDate = {};
  events.forEach(ev => {
    if (!eventsByDate[ev.date]) eventsByDate[ev.date] = [];
    eventsByDate[ev.date].push(ev);
  });

  const goPrev = () => {
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    onMonthChange(y, m);
  };
  const goNext = () => {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    onMonthChange(y, m);
  };
  const goToday = () => {
    const now = new Date();
    onMonthChange(now.getFullYear(), now.getMonth());
    onSelectDate(todayStr);
  };

  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #f3f4f6', overflow: 'hidden', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={goPrev} style={navBtnStyle} aria-label="Previous month">◀</button>
          <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 16, fontWeight: 700, color: '#0f172a', minWidth: 140, textAlign: 'center' }}>
            {MONTH_NAMES[month]} {year}
          </span>
          <button onClick={goNext} style={navBtnStyle} aria-label="Next month">▶</button>
          <button onClick={goToday} style={{ ...navBtnStyle, width: 'auto', padding: '0 12px', fontSize: 12, fontWeight: 700, color: '#059669' }}>
            Today
          </button>
        </div>
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {WEEKDAYS.map(w => (
          <div key={w} style={{ textAlign: 'center', padding: 10, fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', borderBottom: '1px solid #f3f4f6' }}>
            {w}
          </div>
        ))}
        {cells.map((cell, i) => {
          const dayEvents = eventsByDate[cell.date] || [];
          const isToday = cell.date === todayStr;
          const isSelected = cell.date === selectedDate;
          const visible = dayEvents.slice(0, maxEventsPerDay);
          const extra = dayEvents.length - visible.length;

          return (
            <div
              key={cell.date + i}
              onClick={() => onSelectDate(cell.date)}
              style={{
                minHeight: 82, padding: 8, cursor: 'pointer',
                borderRight: '1px solid #f3f4f6', borderBottom: '1px solid #f3f4f6',
                background: isSelected ? '#d1fae5' : isToday ? '#f0fdf4' : cell.otherMonth ? '#fafafa' : '#fff',
                transition: 'background .15s',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f0fdf4'; }}
              onMouseLeave={e => { e.currentTarget.style.background = isSelected ? '#d1fae5' : isToday ? '#f0fdf4' : cell.otherMonth ? '#fafafa' : '#fff'; }}
            >
              <div style={{
                fontSize: 13, fontWeight: 700, width: 24, height: 24, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: cell.otherMonth ? '#d1d5db' : isToday ? '#fff' : '#374151',
                background: isToday ? '#0e9f6e' : 'transparent',
              }}>
                {cell.day}
              </div>
              {visible.map((ev, idx) => {
                const tone = TONE_STYLES[ev.tone] || TONE_STYLES.green;
                return (
                  <div key={idx} title={ev.label} style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 5px', borderRadius: 4, marginTop: 3,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    background: tone.bg, color: tone.color,
                  }}>
                    {ev.label}
                  </div>
                );
              })}
              {extra > 0 && (
                <div style={{ fontSize: 10, fontWeight: 600, padding: '2px 5px', borderRadius: 4, marginTop: 3, background: '#dbeafe', color: '#1e40af' }}>
                  +{extra} more
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ padding: '12px 18px', display: 'flex', gap: 16, borderTop: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
        <LegendItem color="#d1fae5" label="Upcoming" />
        <LegendItem color="#dbeafe" label="Completed" />
        <LegendItem color="#fee2e2" label="Cancelled / No-show" />
      </div>
    </div>
  );
};

const navBtnStyle = {
  width: 30, height: 30, borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff',
  cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const LegendItem = ({ color, label }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#374151' }}>
    <span style={{ width: 10, height: 10, borderRadius: 2, background: color, display: 'inline-block' }} />
    {label}
  </span>
);

export default AppointmentCalendar;